import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { enrollmentApi } from "@/lib/enrollmentApi";
import { statePath } from "@/lib/stateRegistry";


/**
 * Coming-soon page for a state/track. Shows NO plan data and NO quote intake
 * (blueprint 1.1): only an email waitlist. Never rendered for an active track.
 */
export default function WaitlistPage({ state, track }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }
    setError("");
    setStatus("sending");
    try {
      await enrollmentApi.joinWaitlist({ stateCode: state.code, track, email: email.trim() });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError(
        err && err.kind === "indeterminate"
          ? "We could not confirm your sign-up. It may have gone through. You can submit again."
          : "We could not save your email right now. Please try again later."
      );
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-20" data-testid="waitlist-page" data-state={state.slug} data-track={track}>
      <Seo title="Plan Comparison: Coming Soon" path={statePath(state.slug, track === "commercial" ? "commercial" : "plans")} noindex />
      <span className="pill mb-5">Coming soon</span>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-[#0A2540]">
        Texas plan comparison is coming soon.
      </h1>
      <p className="mt-4 text-lg text-slate-600 max-w-[60ch]">
        Plan comparison and switching are coming soon. Today, RateCurrent helps Texas electricity shoppers look up their meter's ESID and understand their electric bill. Leave your email and we will tell you when plan comparison opens.
      </p>

      {status === "done" ? (
        <div role="status" className="mt-8 max-w-md flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="waitlist-done">
          <CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-[#0A2540]">Thanks. We will email you when {state.name} opens.</p>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="mt-8 max-w-md" data-testid="waitlist-form">
          <label htmlFor="waitlist-email" className="text-overline text-zinc-500 mb-1 block">Email</label>
          <input
            id="waitlist-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={error ? "waitlist-error" : undefined}
            className="input-brutal w-full"
            data-testid="waitlist-email"
          />
          {error && <p id="waitlist-error" role="alert" className="text-sm text-red-700 mt-2">{error}</p>}
          <button type="submit" disabled={status === "sending"} className="btn-green mt-4" data-testid="waitlist-submit">
            {status === "sending" ? "Sending…" : "Notify me"}
          </button>
        </form>
      )}
      <p className="mt-10 text-sm">
        <Link to="/texas/esid-lookup" className="underline">Back to ESID lookup and bill check</Link>
      </p>
    </div>
  );
}
